/**
 *
 * @module userActivityApi
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/user-activity.md
 * @see https://github.bamtech.co/sdk-distribution/bam-sdk/blob/master/Features/UserActivityApi.md
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';
import UserActivityEvent from './userActivityEvent';
import EventSchemataProvider from './eventSchemataProvider';

const DustLogUtility = Services.Internal.Dust.DustLogUtility;
const DustUrnReference = Services.Internal.Dust.DustUrnReference;
const DustCategory = Services.Internal.Dust.DustCategory;
const PersonalizationApiDustUrnReference = DustUrnReference.userActivity.personalization;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    array,
    object,
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class UserActivityApi
 * @desc The User Activity Api is a public interface for DUST. This api will allow app devs to send custom events,
 * related to user activity while using an app.
 * @since 3.4.0
 *
 */
export default class UserActivityApi {

    /**
     *
     * @param {SDK.Logging.Logger} [logger=Logging]
     *
     */
    constructor(logger = Logging) {

        const errorMessage = {
            ctor: 'UserActivityApi(logger)',
            get logger() {
                return `${this.ctor} needs Logger instance`;
            }
        };

        instanceStrict(logger, Logging.Logger, errorMessage.logger, InvalidArgumentException);

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {Object}
         * @desc global tracking parameters for Glimpse that we try to apply to all Glimpse payloads
         * @note event specific tracking parameters will overwrite what is in here
         * @note spec says public but we mark this private because we want you to use `this.setTrackingParameters`
         *
         */
        this.trackingParameters = {};

        /**
         *
         * @access public
         * @type {SDK.UserActivity.EventSchemataProvider}
         * @desc stores and uses schemata to generate event payloads
         * @readonly
         *
         */
        this.schemataProvider = new EventSchemataProvider();

        /**
         *
         * @access private
         * @type {Boolean}
         * @note needs to default to true to collect dust events before the configuration is fetched and we can
         * determine if this should be enabled
         *
         */
        this.dustEnabled = true;

        this.logger.info(UserActivityApi.name, 'Created.');

    }

    /**
     *
     * @access public
     * @param {String} rewardToken - The reward token associated with this content recommendation.
     * @param {String} contentId - The ID of content being rewarded, by the defined action.
     * @param {String} action - The action of the reward.
     * @param {Array<String>} recommendedContentIds - Array of content id(s) that were recommended and viewable
     * when the user action occurred.
     * @desc Posts an event for a users content recommendation reward.
     * @returns {Promise<Object>} returns the payload that is sent to dust (for debugging purposes only)
     *
     */
    sendContentReward(rewardToken, contentId, action, recommendedContentIds) {

        const errorMessage = {
            method: 'UserActivityApi.sendContentReward(rewardToken, contentId, action, recommendedContentIds)',
            get rewardToken() {
                return `${this.method} rewardToken should be a String with length`;
            },
            get contentId() {
                return `${this.method} contentId should be a String with length`;
            },
            get action() {
                return `${this.method} action should be a String with length`;
            },
            get recommendedContentIds() {
                return `${this.method} recommendedContentIds should be an Array of Strings with length`;
            }
        };

        nonEmptyString(rewardToken, errorMessage.rewardToken, InvalidArgumentException);
        nonEmptyString(contentId, errorMessage.contentId, InvalidArgumentException);
        nonEmptyString(action, errorMessage.action, InvalidArgumentException);
        array.of.nonEmptyString(recommendedContentIds, errorMessage.recommendedContentIds, InvalidArgumentException);

        const { logger, dustEnabled } = this;

        const data = {
            rewardToken,
            rewardContentId: contentId,
            action,
            recommendedContentIds
        };

        if (dustEnabled) {

            const dustLogUtility = new DustLogUtility({
                category: DustCategory.personalization,
                logger,
                source: UserActivityApi.name,
                urn: PersonalizationApiDustUrnReference.contentRecommendationAward,
                data
            });

            dustLogUtility.log();

        }

        return Promise.resolve(data);

    }

    /**
     *
     * @access public
     * @since 3.8.0
     * @param {SDK.UserActivity.UserActivityEvent} event
     * @param {Object} parameters
     * @note we provide an additional `_failures` array on the debugging payload that is returned so the application
     * developer can determine if any schema values were not generated properly
     * @note generally we avoid using underscore pre-fixed names like `_failures` but in this case it's done to ensure
     * we don't accidentally overwrite a value provided by the app dev that is called `failures`
     * @returns {Promise<Object>} returns the payload that is sent to dust (for debugging purposes only)
     *
     */
    trackEvent(event, parameters = {}) {

        const errorMessage = {
            method: 'UserActivityApi.trackEvent(event, parameters)',
            get event() {
                return `${this.method} event should be an instance of SDK.UserActivity.UserActivityEvent`;
            },
            get parameters() {
                return `${this.method} parameters should be an Object`;
            }
        };

        const { logger, dustEnabled, schemataProvider } = this;

        instanceStrict(event, UserActivityEvent, errorMessage.event, InvalidArgumentException);
        object(parameters, errorMessage.parameters, InvalidArgumentException);

        const globalTrackingData = Object.assign({}, this.trackingParameters, parameters);
        const schemata = schemataProvider.getSchemata(event);
        const debuggingPayload = schemata.generateEventPayload(globalTrackingData);

        if (dustEnabled) {

            const dustLogUtility = new DustLogUtility({
                category: DustCategory.glimpse,
                logger,
                source: UserActivityApi.name,
                urn: event.eventUrn,
                data: {
                    ...debuggingPayload,
                    _failures: undefined // make sure `_failures` is not included
                }
            });

            dustLogUtility.log();

        }

        return Promise.resolve(debuggingPayload);

    }

    /**
     *
     * @access public
     * @since 3.8.0
     * @param {Object} trackingParameters
     * @desc merges the input to this function with the trackingParameters field on the UserActivityApi - this will
     * overwrite any existing duplicate keys that currently exist
     * @note this method is not in the spec - we provide a setter so app devs don't modify the UserActivityApi instance
     * directly
     *
     */
    setTrackingParameters(trackingParameters) {

        const errorMessage = {
            method: 'UserActivityApi.setTrackingParameters(trackingParameters)',
            get trackingParameters() {
                return `${this.method} trackingParameters should be an Object`;
            }
        };

        object(trackingParameters, errorMessage.trackingParameters, InvalidArgumentException);

        this.trackingParameters = Object.assign(this.trackingParameters, trackingParameters);

    }

}



// WEBPACK FOOTER //
// ./src/userActivity/userActivityApi.js